/**
 * Instant apply / unassign from the Scheduler. Assign mirrors the config
 * save's cache reasoning: seed the config cache with the returned config,
 * invalidate the queue (computed FROM config), the scheduler list (badge +
 * formula summary changed) and the library (campaign counts moved).
 * Unassign is the plain config PUT with packageId: null — values untouched.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  FlowOptimizerConfigListItem,
  FlowOptimizerLibraryProvider,
} from '@trafficpulse/shared';

import { QK } from '@/features/flow-optimizer/hooks/useFlowOptimizerConfig';
import { queueKeys } from '@/features/flow-optimizer/hooks/useFlowOptimizerQueue';
import { upsertConfig } from '@/features/flow-optimizer/services/flow-optimizer-api';
import { optimizerCenterKeys } from '@/features/optimizer-center/hooks/useOptimizerConfigs';

import { applyPackage } from '../api/client/packages-api';
import { libraryKeys } from '../api/keys';

export interface AssignTarget {
  provider: FlowOptimizerLibraryProvider;
  campaignId: string;
  flowId: string;
}

/** Write the server-confirmed package straight into the scheduler list row.
 *  The invalidations below refetch too, but a refetch is timing-dependent —
 *  QA caught a stale badge surviving an unassign — while this is instant and
 *  carries only what the mutation proved. */
export function patchRowPackage(
  queryClient: QueryClient,
  target: Pick<AssignTarget, 'provider' | 'campaignId'>,
  pkg: FlowOptimizerConfigListItem['package'],
): void {
  queryClient.setQueryData<FlowOptimizerConfigListItem[]>(
    optimizerCenterKeys.configs(target.provider),
    (rows) =>
      rows?.map((row) =>
        row.campaignId === target.campaignId ? { ...row, package: pkg } : row,
      ),
  );
}

/** The list also derives mode/formula from config — best-effort colors come
 *  from the packages cache (falls back to no dot until the refetch lands). */
export function cachedPackageColor(
  queryClient: QueryClient,
  packageId: string,
): string | null {
  const packages = queryClient.getQueryData<Array<{ id: string; color?: string | null }>>(
    libraryKeys.packages(),
  );
  return packages?.find((pkg) => pkg.id === packageId)?.color ?? null;
}

export function invalidateAfterApply(
  queryClient: QueryClient,
  target: Pick<AssignTarget, 'provider' | 'campaignId'>,
): void {
  void queryClient.invalidateQueries({
    queryKey: queueKeys.queue(target.provider, target.campaignId),
  });
  void queryClient.invalidateQueries({
    queryKey: optimizerCenterKeys.configs(target.provider),
  });
  void queryClient.invalidateQueries({ queryKey: libraryKeys.all });
}

export function useAssignPackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { packageId: string; target: AssignTarget }) =>
      applyPackage(input.packageId, {
        provider: input.target.provider,
        campaignId: input.target.campaignId,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(
        QK.config(result.provider, result.campaignId),
        result.config,
      );
      patchRowPackage(queryClient, result, {
        id: result.packageId,
        name: result.packageName,
        color: cachedPackageColor(queryClient, result.packageId),
      });
      invalidateAfterApply(queryClient, result);
    },
  });
}

export function useUnassignPackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (target: AssignTarget) =>
      upsertConfig(
        target.campaignId,
        { flowId: target.flowId, packageId: null },
        target.provider,
      ),
    onSuccess: (config, target) => {
      queryClient.setQueryData(QK.config(target.provider, target.campaignId), config);
      patchRowPackage(queryClient, target, null);
      invalidateAfterApply(queryClient, target);
    },
  });
}
